import { isBackendGatewayError } from '../../utils/backendGatewayError';

const REPORT_ABSENCE_CODES = new Set([
  'not-found',
  'functions/not-found',
  'report/not-found',
  'report/report-absent',
]);
const REPORT_ABSENCE_REASONS = new Set([
  'not-found',
  'report/not-found',
  'report-absent',
  'report/report-absent',
]);

const getErrorCode = (error: unknown): string | null => {
  if (!error || typeof error !== 'object') {
    return null;
  }

  const code = (error as { code?: unknown }).code;
  return typeof code === 'string' ? code : null;
};

const getStructuredReasons = (error: unknown): string[] => {
  if (!error || typeof error !== 'object') {
    return [];
  }

  const record = error as Record<string, unknown>;
  const reasons: string[] = [];
  if (typeof record.reason === 'string') {
    reasons.push(record.reason);
  }

  const detailSources = [
    record.details,
    (record.customData as Record<string, unknown> | undefined)?.details,
  ];

  for (const source of detailSources) {
    if (!Array.isArray(source)) {
      continue;
    }

    for (const detail of source) {
      if (
        detail &&
        typeof detail === 'object' &&
        typeof (detail as Record<string, unknown>).reason === 'string'
      ) {
        reasons.push((detail as Record<string, unknown>).reason as string);
      }
    }
  }

  return reasons;
};

const isReportAbsenceGatewayError = (error: unknown): boolean =>
  isBackendGatewayError(error) &&
  error.operation === 'report' &&
  REPORT_ABSENCE_REASONS.has(error.reason);

const isAuthoritativeReportAbsenceErrorInner = (error: unknown, seen: WeakSet<object>): boolean => {
  if (!error || typeof error !== 'object') {
    return false;
  }

  if (seen.has(error)) {
    return false;
  }
  seen.add(error);

  const code = getErrorCode(error);
  if (code && REPORT_ABSENCE_CODES.has(code)) {
    return true;
  }

  if (getStructuredReasons(error).some((reason) => REPORT_ABSENCE_REASONS.has(reason))) {
    return true;
  }

  if (isReportAbsenceGatewayError(error)) {
    return true;
  }

  if (isBackendGatewayError(error)) {
    return isAuthoritativeReportAbsenceErrorInner(error.original, seen);
  }

  return false;
};

export const isAuthoritativeReportAbsenceError = (error: unknown): boolean =>
  isAuthoritativeReportAbsenceErrorInner(error, new WeakSet<object>());
